import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { type ReactElement, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { buildClerkAppearance } from '@/auth/clerkAppearance';
import { AuthProvider } from '@/auth/useAuth';
import { OrganisationFilterProvider } from '@/features/organisations/providers/OrganisationFilterProvider';
import { ProjectFilterProvider } from '@/features/projects';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { r } from '@/theme/tokens/radii';
import { App } from './App';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { NotificationProvider } from './providers/NotificationProvider';
import { createAppQueryClient } from './shared/data/queryClient';
import { useThemeMode } from './theme/ThemeModeContext';

const queryClient = createAppQueryClient();
export const AppRoot = ({ clerkPublishableKey }: { clerkPublishableKey: string }): ReactElement => {
  const { mode } = useThemeMode();
  const clerkPrimaryColor = '#008080';
  const passwordToggleColor = mode === 'dark' ? '#f2f2f2' : '#1a1a1a';
  const resendCodeColor = mode === 'dark' ? '#27d3d3' : '#007070';
  const cardRadius = r('card');
  const controlRadius = r('md');
  const clerkAppearance = useMemo(
    () =>
      buildClerkAppearance({
        mode,
        cardRadius,
        controlRadius,
        clerkPrimaryColor,
        passwordToggleColor,
        resendCodeColor,
      }),
    [cardRadius, clerkPrimaryColor, controlRadius, mode, passwordToggleColor, resendCodeColor],
  );

  const appContent = (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      afterSignOutUrl="/sign-in"
      appearance={clerkAppearance}
    >
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <AuthProvider>
            <OrganisationFilterProvider>
              <ProjectFilterProvider>
                <AppErrorBoundary>
                  <App />
                </AppErrorBoundary>
              </ProjectFilterProvider>
            </OrganisationFilterProvider>
          </AuthProvider>
        </NotificationProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );

  return (
    <BrowserRouter>
      <ThemeProvider mode={mode} density="compact">
        {appContent}
      </ThemeProvider>
    </BrowserRouter>
  );
};
